import React, { useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { WLContext } from '@contexts';
import { Button } from '@design-system';

interface AuthActionLinkProps {
  /**
   * Main text/question (e.g., "¿Aún no tienes una cuenta?")
   */
  label: string;
  /**
   * Action text (e.g., "Regístrate gratis")
   */
  actionText: string;
  /**
   * Optional destination path for router Link
   */
  to?: string;
  /**
   * Optional click handler for Button style link
   */
  onClick?: () => void;
  /**
   * Disable the action (for Button style)
   */
  isDisabled?: boolean;
  /**
   * Additional container styles
   */
  className?: string;
}

import { AuthPageContext } from '../context';

export const AuthActionLink: React.FC<AuthActionLinkProps> = ({
  label,
  actionText,
  to,
  onClick,
  isDisabled,
  className,
}) => {
  const { color } = useContext(WLContext);
  const authPageContext = useContext(AuthPageContext);
  const CustomLink = Link as any;

  // Use ref to keep stable reference to the latest onClick
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;

  const handleClick = useCallback(() => {
    onClickRef.current?.();
  }, []);

  const content = useMemo(() => (
    <div className={`flex items-baseline justify-center gap-1.5 text-sm font-medium ${className || ''}`}>
      <span className="text-gray-300">{label}</span>
      {to ? (
        <CustomLink className="font-bold whitespace-nowrap" style={{ color }} to={to}>
          {actionText}
        </CustomLink>
      ) : (
        <Button
          color={color}
          onClick={handleClick}
          isDisabled={isDisabled}
          size="sm"
          variant="link"
          className="font-bold p-0 lg:h-auto whitespace-nowrap"
        >
          {actionText}
        </Button>
      )}
    </div>
  ), [label, actionText, to, handleClick, isDisabled, className, color]);

  // If we are inside the AuthPageContext, we "teleport" the content to the footer slot
  useEffect(() => {
    if (authPageContext) {
      authPageContext.setFooterAction(content);
      // Cleanup on unmount
      return () => authPageContext.setFooterAction(null);
    }
  }, [content, authPageContext]);

  // If we have the context, we return null here because the content is rendered in the slot
  if (authPageContext) {
    return null;
  }

  return content;
};
